import { useCallback, useEffect, useRef, useState } from "react";

import { addBridgeListener, sendToExtension } from "./extensionBridge";
import type { AuthTokenPayload, ExtensionMessage } from "./types";

interface AuthBridgeReturn {
  isAuthenticated: boolean;
  isLoading: boolean;
  accessToken: string | null;
  account: { name: string; username: string } | null;
  login: () => void;
  logout: () => void;
}

/**
 * Auth hook for extension-embedded mode.
 * Receives tokens via postMessage from the extension shell.
 */
export function useAuthBridge(): AuthBridgeReturn {
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [account, setAccount] = useState<{ name: string; username: string } | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loginPending, setLoginPending] = useState(false);
  const initialized = useRef(false);

  useEffect(() => {
    // Fallback give-up: if the shell never speaks, stop loading and let the app
    // decide app-vs-login. Two windows: a short blind wait when we don't yet know
    // an extension is even there, and a LONGER wait once the shell has told us a
    // restore is actively in flight (AUTH_RESTORING) — a silent SSO round-trip
    // can exceed the short window, and giving up early is what flashed the login
    // screen on reopen before the late token arrived.
    const INITIAL_FALLBACK_MS = 5_000;
    const RESTORING_FALLBACK_MS = 15_000;

    let timer: ReturnType<typeof setTimeout>;
    const settle = () => {
      if (!initialized.current) {
        initialized.current = true;
        setIsLoading(false);
      }
    };
    const armFallback = (ms: number) => {
      clearTimeout(timer);
      timer = setTimeout(settle, ms);
    };

    const cleanup = addBridgeListener((msg: ExtensionMessage) => {
      switch (msg.type) {
        case "AUTH_TOKEN": {
          const payload = msg.payload as AuthTokenPayload;
          clearTimeout(timer);
          setAccessToken(payload.accessToken);
          setAccount(payload.account);
          setLoginPending(false);
          settle();
          break;
        }
        case "AUTH_CLEARED":
          clearTimeout(timer);
          setAccessToken(null);
          setAccount(null);
          setLoginPending(false);
          settle();
          break;
        case "AUTH_RESTORING":
          // Shell is actively restoring a session — keep the spinner and push the
          // give-up deadline out so a slow silent SSO doesn't flash /login. The
          // following AUTH_TOKEN / AUTH_CLEARED settles us for real.
          if (!initialized.current) armFallback(RESTORING_FALLBACK_MS);
          break;
      }
    });

    armFallback(INITIAL_FALLBACK_MS);

    // Announce readiness and PULL the current auth state. The app shell's
    // `signalReady()` (IFRAME_READY) fires at app-boot, but THIS listener mounts
    // later — so on a fast token restore the shell's AUTH_TOKEN push can arrive
    // before we're listening and be lost, leaving a valid session stuck on the
    // login screen every reopen. Requesting once our listener is live closes
    // that race: the shell replies with AUTH_TOKEN (or AUTH_RESTORING) now.
    try {
      sendToExtension("REQUEST_TOKEN_REFRESH");
    } catch {
      /* not embedded / no parent — ignore */
    }

    return () => {
      cleanup();
      clearTimeout(timer);
    };
  }, []);

  const login = useCallback(() => {
    if (loginPending) return;
    setLoginPending(true);
    sendToExtension("REQUEST_LOGIN");
  }, [loginPending]);
  const logout = useCallback(() => sendToExtension("REQUEST_LOGOUT"), []);

  return {
    isAuthenticated: !!accessToken,
    isLoading: isLoading || loginPending,
    accessToken,
    account,
    login,
    logout,
  };
}
